import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import LazyImage from '../common/LazyImage';

import comp1 from '../../images/one.gif';
import comp3 from '../../images/three.gif';
import comp2 from '../../images/two.gif';

export default function HeroSection() {
  const [website, setWebsite] = useState('');
  const [activeDemo, setActiveDemo] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const navigate = useNavigate();
  const heroRef = useRef(null);

  // URL validation function
  const isValidUrl = (string) => {
    try {
      const url = new URL(string);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_) {
      return false;
    }
  };

  const demoScreens = [
    {
      image: comp1,
      title: 'Deal Progress',
      subtitle: 'Track every milestone',
      color: 'from-blue-400 to-blue-600',
    },
    {
      image: comp2,
      title: 'Buyer Insights',
      subtitle: "Know who's interested",
      color: 'from-emerald-400 to-emerald-600',
    },
    {
      image: comp3,
      title: 'Document Center',
      subtitle: 'Secure data room',
      color: 'from-purple-400 to-purple-600',
    },
  ];

  // Mouse parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
        const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
        setMousePosition({ x: x * 20, y: y * 20 });
      }
    };

    if (isHovering) {
      window.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isHovering]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveDemo((prev) => (prev + 1) % 3);
        setIsTransitioning(false);
      }, 300);
    }, 4000); // Change every 4 seconds
    return () => clearInterval(interval);
  }, []);

  const handleGetValuation = () => {
    if (website) {
      // Navigate to valuation page with website as query parameter and auto-start analysis
      navigate(`/valuation?website=${encodeURIComponent(website)}&autoStart=true`);
    } else {
      // Navigate to valuation page without website
      navigate('/valuation');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="py-10 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="fade-in">
            <div className="mb-2">
              <span className="bg-amiva-gradient-subtle text-amiva-primary text-sm font-semibold px-5 py-2.5 rounded-full inline-flex items-center shadow-sm">
                <span className="w-2 h-2 bg-amiva-accent rounded-full mr-2.5 animate-pulse"></span>
                Boutique Sell-Side Advisory
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-gray-900 mb-5 leading-tight">
              Get an offer in
              <span className="text-gradient block lg:inline"> Under 3 Weeks</span>
            </h1>
            <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 mb-6 lg:mb-8 leading-relaxed max-w-2xl">
              We curate thousands of strategic buyers through our confidential process, delivering
              exceptional service with complete transparency via our AI-native platform, AMIVA+.
            </p>

            {/* Website Input - Enhanced */}
            <div className="mb-10">
              <p className="text-base font-semibold text-gray-700 mb-4">
                Get your confidential business valuation
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="url"
                  placeholder="https://yourbusiness.com"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  onBlur={(e) => {
                    // Auto-add https:// if not present
                    let url = e.target.value.trim();
                    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
                      url = 'https://' + url;
                      setWebsite(url);
                    }
                  }}
                  className="form-input flex-1 text-base sm:text-lg px-5 py-4 shadow-sm"
                  style={{ fontSize: '1.05rem' }}
                  pattern="https?://.+"
                  title="Please enter a valid URL starting with http:// or https://"
                />
                <button
                  onClick={handleGetValuation}
                  disabled={!website || !isValidUrl(website)}
                  className={`px-8 sm:px-10 py-4 text-base sm:text-lg whitespace-nowrap transition-all duration-200 ${
                    website && isValidUrl(website)
                      ? 'btn-primary'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Get Free Valuation
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-4 sm:gap-6 mt-4 text-xs sm:text-sm text-gray-500">
                <span className="flex items-center">
                  <svg
                    className="w-4 h-4 mr-1 text-green-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Confidential process
                </span>
                <span className="flex items-center">
                  <svg
                    className="w-4 h-4 mr-1.5 text-green-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  No obligations
                </span>
                <span className="flex items-center">
                  <svg
                    className="w-4 h-4 mr-1.5 text-green-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Expert guidance
                </span>
              </div>
            </div>

            {/* Trust Indicators */}
            <div>
              <p className="text-sm font-medium text-gray-500 mb-5 uppercase tracking-wider">
                As Seen On
              </p>
              <div className="flex flex-wrap items-center gap-8 opacity-60 grayscale hover:grayscale-0 transition-all duration-300">
                <span className="text-xl font-bold text-gray-700">MarketWatch</span>
                <span className="text-xl font-bold text-gray-700">Yahoo!</span>
                <span className="text-xl font-bold text-gray-700">Bloomberg</span>
                <span className="text-xl font-bold text-gray-700">Digital Journal</span>
                <span className="text-xl font-bold text-gray-700">Benzinga</span>
                <span className="text-xl font-bold text-gray-700">Business Insider</span>
              </div>
            </div>
          </div>

          {/* Hero Visual - Enhanced with Parallax */}
          <div
            ref={heroRef}
            className="relative lg:pl-8 fade-in"
            style={{ animationDelay: '0.2s' }}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <div
              className="relative"
              style={{
                transform: `translateX(${mousePosition.x}px) translateY(${mousePosition.y}px)`,
                transition: 'transform 0.3s ease-out',
              }}
            >
              {/* Animated Background gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-amiva-primary/10 to-amiva-accent/10 rounded-3xl transform rotate-3 scale-105 animate-pulse"></div>

              {/* Main dashboard preview */}
              <div className="relative">
                {/* Browser window frame */}
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-500 hover:shadow-3xl">
                  {/* Browser header */}
                  <div className="bg-gray-100 px-4 py-3 flex items-center gap-2 border-b border-gray-200">
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-400 hover:scale-110 transition-transform cursor-pointer"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-400 hover:scale-110 transition-transform cursor-pointer"></div>
                      <div className="w-3 h-3 rounded-full bg-green-400 hover:scale-110 transition-transform cursor-pointer"></div>
                    </div>
                    <div className="flex-1 ml-4">
                      <div className="bg-white rounded-md px-3 py-1 text-xs text-gray-600 font-medium hover:bg-gray-50 transition-colors">
                        app.amivaplus.com/dashboard
                      </div>
                    </div>
                  </div>

                  {/* Dashboard content with smooth transitions */}
                  <div className="relative overflow-hidden" style={{ height: '300px' }}>
                    <LazyImage
                      src={demoScreens[activeDemo].image}
                      alt={`AMIVA+ Platform Dashboard - ${demoScreens[activeDemo].title}: ${demoScreens[activeDemo].subtitle} for M&A deals`}
                      className={`w-full h-full object-cover object-top transition-all duration-700 ${
                        isTransitioning ? 'opacity-0 scale-105' : 'opacity-100 scale-100'
                      }`}
                      priority={true}
                    />

                    {/* Animated Overlay cards */}
                    <div
                      className={`absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg transform transition-all duration-500 ${
                        isTransitioning ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'
                      } hover:scale-105 hover:rotate-1`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 bg-gradient-to-br ${demoScreens[activeDemo].color} rounded-full flex items-center justify-center animate-pulse`}
                        >
                          <svg
                            className="w-6 h-6 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="font-bold text-sm text-gray-900">
                            {demoScreens[activeDemo].title}
                          </p>
                          <p className="text-xs text-gray-600">
                            {demoScreens[activeDemo].subtitle}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Live indicator with glow effect */}
                    <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg flex items-center gap-2 hover:scale-105 transition-transform">
                      <div className="relative">
                        <div className="absolute inset-0 w-2 h-2 bg-emerald-500 rounded-full animate-ping"></div>
                        <div className="relative w-2 h-2 bg-emerald-500 rounded-full"></div>
                      </div>
                      <span className="text-xs font-semibold text-gray-700">Live Updates</span>
                    </div>

                    {/* Animated Progress indicator */}
                    <div
                      className={`absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg transform transition-all duration-500 ${
                        isTransitioning ? 'translate-y-full opacity-0' : 'translate-y-0 opacity-100'
                      } hover:scale-105`}
                    >
                      <div className="text-center">
                        <div className="text-2xl font-bold text-amiva-primary animate-pulse">
                          12/15
                        </div>
                        <div className="text-xs text-gray-600">NDAs Signed</div>
                      </div>
                    </div>

                    {/* Enhanced Screen indicators */}
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white/95 backdrop-blur-sm rounded-full px-3 py-2 shadow-lg">
                      <div className="flex gap-2">
                        {demoScreens.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              setIsTransitioning(true);
                              setTimeout(() => {
                                setActiveDemo(index);
                                setIsTransitioning(false);
                              }, 300);
                            }}
                            className={`rounded-full transition-all duration-500 ${
                              index === activeDemo
                                ? 'bg-gradient-to-r from-amiva-primary to-amiva-accent w-8 h-3'
                                : 'bg-gray-300 hover:bg-gray-400 w-3 h-3 hover:scale-125'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Enhanced Floating elements */}
                <div className="absolute -top-4 -right-4 bg-gradient-to-br from-amiva-accent to-emerald-600 text-white rounded-full p-3 shadow-xl animate-bounce hover:scale-110 transition-transform cursor-pointer">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                    <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
